import { Mui, MuiIcons } from '@osu/react-ui';
import { useState } from 'react';
import useRoleAuthentication from '../../../Common/hooks/useRoleAuthentication';
import { TOOLTIPS } from "../../../util/constants";
import { handleKeyPresses } from "./rechist-treegrid-accessibility";

const styles = {
  screenReaderOnly: {
    left: "-9999px",
    position: "absolute !important",
  },
  removeLastChildBottomBorder: {
    "& td:first-of-type": { textAlign: "center" },
    "&:nth-last-of-type(1 of .hasBottomLine) td": { borderBottom: "none" },
    "& td": {
      paddingTop: "0.2rem",
      paddingBottom: "0.2rem",
    },
  },
  thrReview: {
    textAlign: "center",
    width: "5rem",
  },
  thrTime: { width: "13rem" },
  verticallyCenter: {
    display: "flex",
    alignItems: "center",
  },
  clickToExpandRow: { cursor: "pointer" },
  expandCollapse: { width: "52px" },
  expandableRow: {
    "& td": {
      borderBottom: "none",
      padding: "0",
    },
  },
  expandedRowDataWrapper: {
    backgroundColor: '#f2f2f2',
    padding: "16px",
    "& span": {
      paddingTop: "4px",
      paddingBottom: "4px",
    },
  },
  expandableBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  section: {
    display: "flex",
    flexDirection: 'column',
    width: '25%',
  },
  indent: { paddingLeft: "2rem" },
  invisibleButton: { visibility: "hidden" },
};

const HistoryTable = (props) => {
  const { recommendationHistory } = props?.studentData?.data ?? {};
  // console.log("recommendationHistory:", recommendationHistory)
  
  return recommendationHistory.length === 0 ? (
    'No recommendations have been submitted for this application.'
  ) : (
    <Mui.Table aria-label="Recommendation History" role="treegrid">
        <Mui.TableHead>
            <Mui.TableRow>
                <Mui.TableCell sx={styles.thrReview}>Review</Mui.TableCell>
                <Mui.TableCell sx={styles.thrTime}>Assign Time</Mui.TableCell>
                <Mui.TableCell sx={styles.thrTime}>Submit Time</Mui.TableCell>
                <Mui.TableCell>Name</Mui.TableCell>
                <Mui.TableCell>Recommendation</Mui.TableCell>
                <Mui.TableCell aria-label="Button to expand/collapse row" sx={styles.expandCollapse}></Mui.TableCell>
            </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
            {
                recommendationHistory.map((row, index, allRows) => {
                    return ( <HistoryReviewerDoubleRow key={index} row={row} index={index} allRows={allRows} /> );
                })
            }
        </Mui.TableBody>
    </Mui.Table>
  );
};

function HistoryReviewerDoubleRow({row, index, allRows}) {
    const [expanded, setExpanded] = useState(false);
    const authDetails = useRoleAuthentication();
    const canExpand = authDetails.ADMIN;

    function flipExpanded(event) { setExpanded(canExpand && !this); event.preventDefault(); event.stopPropagation(); }
    function getExpandedButton(expanded) { return expanded ? <MuiIcons.KeyboardArrowUp /> : <MuiIcons.KeyboardArrowDown /> }
    
    const parentRowSx = { ...styles.removeLastChildBottomBorder, ...(canExpand ? styles.clickToExpandRow : {}) };
    const buttonSx = canExpand ? {} : styles.invisibleButton;
    const keyPressHandler = handleKeyPresses.bind({ expanded, setExpanded });
    const colSpanCount = 5 + (canExpand ? 1 : 0);
    const showNotificationIcon = row.notificationCreated && (!row.notificationCleared);
    
    return (
        <>
            {/* Main row of reviewer recommendation details */}
            <Mui.TableRow data-rowhasfocusables={"true"} onKeyDown={keyPressHandler} className="hasBottomLine" sx={parentRowSx} onClick={flipExpanded.bind(expanded)} role="row" aria-level="1" aria-posinset={index} aria-setsize={allRows.length}>
                <Mui.TableCell role="gridcell" aria-label={`${expanded ? "Expanded" : "Collapsed"} ${row.stage}`} tabIndex="0">{row.stage}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.assignTimeFormatted}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.submitTimeFormatted}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0"><div sx={styles.verticallyCenter}>{row.nameFormatted}{showNotificationIcon && ( TOOLTIPS('NOTIFY') )}</div></Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.recommendationUnified}</Mui.TableCell>
                <Mui.TableCell role="gridcell">
                    <Mui.IconButton sx={buttonSx} onClick={flipExpanded.bind(expanded)} aria-label={expanded ? "Row Expanded" : "Row Collapsed"} size="small" tabIndex="0">
                        { getExpandedButton(expanded) }
                    </Mui.IconButton>
                </Mui.TableCell>
            </Mui.TableRow>
            
            {/* Expandable row of reviewer notes/details for admins */}
            { canExpand && (
                <Mui.TableRow data-rowhasfocusables={expanded} onKeyDown={keyPressHandler} sx={styles.expandableRow} role="row" aria-expanded={expanded.toString()} aria-level="2" aria-posinset="1" aria-setsize="1" tabIndex="-1">
                    <Mui.TableCell aria-label="Review Notes" colSpan={colSpanCount} role="gridcell" tabIndex="-1">
                        <Mui.Collapse in={expanded} timeout="auto" unmountOnExit>
                            <div sx={styles.expandedRowDataWrapper}>
                                <HistoryRow2ExpandableBox expanded={expanded} row={row} />
                            </div>
                        </Mui.Collapse>
                    </Mui.TableCell>
                </Mui.TableRow>
            )}
        </>
    );
}

function HistoryRow2ExpandableBox({ expanded, row }) {
    const isAppDecisionOverride = row.type === "ApplicationDecisionOverride" && row.adminOverrideComment?.length > 0;
    const commentsExist = isAppDecisionOverride || row.comments?.some?.(section => section.hasNotes);

    if (!commentsExist       ) { return <span data-focusable={expanded} tabIndex="0">There are no associated notes with this review.</span>; }
    if (isAppDecisionOverride) { return <span data-focusable={expanded} tabIndex="0"><b>Administrative Recommendation Comments:</b> {row.adminOverrideComment}</span>; }
    console.info("typ", row, row.adminOverrideComment);
        
    return (
        <>
            <Mui.Typography sx={styles.screenReaderOnly} component="h3">Notes from {row.nameFormatted}</Mui.Typography>
            <Mui.Box sx={styles.expandableBox}>
                { row.comments.map((section, index) => ( <HistoryRowNotes key={index} expanded={expanded} index={index} section={section} /> )) }
            </Mui.Box>
        </>
    );
}

function HistoryRowNotes({ expanded, index, section }) {
    
    return (
        <Mui.Box aria-label={`${section.title} Section`} sx={styles.section} data-focusable={expanded} tabIndex="0">
            <Mui.Typography component="h4" variant="subtitle2">{section.title}</Mui.Typography>
            { section.notes?.map((note, index) => ( <span style={note.shouldIndent ? styles.indent : undefined} key={index}>{note.label}</span> )) }
        </Mui.Box>
    );
}

export default HistoryTable;